import { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { UserRank } from '../types';
import { ArrowLeft, Heart, MessageCircle, Share2, Megaphone, Send, MapPin, Lock, MoreHorizontal, Copy, Ban, Pin, Trash2, ShieldAlert, UserX, X, UserMinus, TrendingUp, Image as ImageIcon, ChevronDown, ChevronUp , Download, Pencil } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { softDeleteYap, reportToWatchers } from '../services/WatcherService';
import { FlagImage } from './utils/countryFlags';
import { AnonymousAvatar, ANONYMOUS_USER } from './AnonymousAvatar';
import { ShareYapModal } from './ShareYapModal';
import { ShoutYapModal } from './ShoutYapModal';
import { ShoutedYapOptionsModal } from './ShoutedYapOptionsModal';
import { DeleteYapModal } from './DeleteYapModal';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { useYapInteractions } from './YapInteractionsContext';
import { useSettings } from './SettingsContext';
import { LegionDeleteModal } from './LegionDeleteModal';
import { toast } from 'sonner';
import { copyToClipboard } from './utils/clipboard';
import { formatTimeAgo, formatFirestoreTime } from './utils/timeFormat';
import { ParallaxBackground } from './utils/parallax';
import { LegionAvatarWrapper } from './LegionAvatarWrapper';
import { getRankRingParams } from './utils/rankUtils';
import { YapService } from '../services/api';
import { StorageService } from '../services/StorageService';
import { AnalyticsService } from '../services/AnalyticsService';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { useEffect } from 'react';
import { useTheme } from './ThemeProvider';
import { LikeButton } from './ui/LikeButton';
import { YapTopicLabel } from './ui/YapTopicLabel';

// Maximum replies allowed per yap
import { ImageWithPreview } from './ImageWithPreview';
import { YapImageGrid } from './ui/YapImageGrid';
import { PollCard } from './ui/PollCard';
import { YapVideoPlayer } from './ui/YapVideoPlayer';
import { FriendBadge } from './ui/FriendBadge';
import { LikersModal } from './ui/LikersModal';
import { getYapAuthorInfo, getYapInteractionId, getYapPostType, isShoutedYap, isQuoteShout } from './utils/yapUtils';
import { EmbeddedYap } from './EmbeddedYap';
import { EVENTS } from '../constants/navigation';
import { FormattedText } from './FormattedText';
import { extractFirstUrl } from './utils/textParsers';
import { LinkPreview } from './LinkPreview';
import { RoomShareCard } from './ui/RoomShareCard';
import { sameBracket } from '../utils/ageBracket';
import RankBadge from './RankBadge';
import { EditPostModal } from './EditPostModal';
import { PostMenu } from './ui/PostMenu';
import { PostReplies } from './PostReplies';

interface YapDetailScreenProps {
  yap: any;
  onBack: () => void;
  onYapUpdate?: () => void;
  isPinnedView?: boolean; // Show pinned badge when viewing from a profile
}

interface Reply {
  id: string;
  authorId: string | number;
  author: string;
  username: string;
  avatar: string;
  content: string;
  time: string;
  likes: number;
  likedBy: (string | number)[];
  authorCountryFlag?: string;
  authorRank?: string;
  type?: 'text' | 'voice';
  audioUrl?: string;
  duration?: number;
  parentReplyId?: string;
  image?: string;
}

export function YapDetailScreen({ yap, onBack, onYapUpdate, isPinnedView = false }: YapDetailScreenProps) {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShoutModal, setShowShoutModal] = useState(false);
  const [showShoutOptionsModal, setShowShoutOptionsModal] = useState(false);
  const [selectedYapToShout, setSelectedYapToShout] = useState<any>(null);
  // Whether any shout of this yap carries a caption — decides whether the
  // "View Shouts" entry point appears at all. Starts false so the button never
  // flashes in and back out while the check is in flight.
  const [hasCaptionedShouts, setHasCaptionedShouts] = useState(false);
  // Likers list being viewed — either a reply's likedBy (userIds) or the yap's
  // likers (loaded async). null = closed.
  const [likers, setLikers] = useState<{ title: string; userIds?: string[]; load?: () => Promise<string[]> } | null>(null);
  const { toggleLike, toggleShout: toggleShoutGlobal, isYapLiked, isYapShouted, getLikeCount, getShoutCount, adjustReplyCount, getPostReplyCount, isPostLiked, getPostLikeCount } = useYapInteractions();
  const { addShout, removeShout, removeShoutLocal, updateShoutCaption, userProfile, blockUser, isTribeMember, isForumMember, isFriend } = useUserData();
  const { isBlockedContent, isHiddenContent } = useContentFilter();
  const { shareLocation } = useSettings();
  
  // Group Interaction Gate (Tribe or Forum)
  const isGroupInteractionAllowed =
    (!yap.tribeId || isTribeMember(yap.tribeId)) &&
    (!yap.forumId || isForumMember(yap.forumId));

  /**
   * Tribe posts take no likes and no replies. Forum posts still take both —
   * tribeId vs forumId is what separates them, since a post carries whichever
   * group it belongs to.
   *
   * This is a display rule, not a security boundary: the rules still permit the
   * writes, so this removes the controls rather than closing the door. Worth
   * knowing before treating it as enforcement.
   */
  const isTribePost = !!(yap as any).tribeId;
  const [editingYapDetail, setEditingYapDetail] = useState(false);
  // The yap arrives as a PROP here, so an edit cannot be pushed back into it.
  // This holds the new text until the feed's own copy catches up, which is what
  // stops the screen still showing the old wording right after saving.
  const [editedYapContent, setEditedYapContent] = useState<string | null>(null);
  
  // Moderate Delete State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { theme } = useTheme();


  // 🔒 FRESH DATA SYNC: Use local state to handle fresh data from Firestore
  // This ensures engagement counts are accurate even when navigated from a stale snapshot
  const [displayYap, setDisplayYap] = useState<any>(yap);
  const [isLoadingYap, setIsLoadingYap] = useState(yap && Object.keys(yap).length <= 2 && yap.id);
  const [authorProfile, setAuthorProfile] = useState<any>(null);
  const [legionDeleteTarget, setLegionDeleteTarget] = useState<{
    yapId: string;
    content: string;
    authorId: string;
  } | null>(null);
  const [legionTrendingLoading, setLegionTrendingLoading] = useState<string | null>(null);

  useScreenReady(!isLoadingYap);

  const handleRemoveFriend = async () => {
    try {
      const { UserService } = await import('../services/api');
      if (window.confirm(`Are you sure you want to remove ${yap.author} as a friend?`)) {
        await UserService.removeFriend(String(userProfile.id), String(yap.userId || yap.authorId));
        toast.success("Friend removed");
      }
    } catch (e) {
      toast.error("Failed to remove friend");
    }
  };

  const handleReportYap = async () => {
    if (!userProfile?.id) return;
    const reason = window.prompt("Why are you reporting this yap? (Harassment, Spam, etc.)");
    if (!reason || reason.trim().length < 4) {
      if (reason !== null) toast.error("A reason is required to report.");
      return;
    }

    try {
      await reportToWatchers(
        String(userProfile.id),
        yap.id,
        'yap',
        reason.trim(),
        {
          authorUsername: yap.author,
          authorId: yap.userId || yap.authorId,
          content: yap.content
        }
      );
      toast.success("Report submitted to Watchers.");
    } catch (err) {
      toast.error("Failed to submit report.");
    }
  };

  // Fetch replies and refresh yap data on mount
  useEffect(() => {
    if (!yap?.id) return;

    // Reset displayYap when yap prop changes
    setDisplayYap(yap);
    
    // Fetch fresh yap data
    const refreshYapData = async () => {
      try {
        const freshYap = await YapService.fetchYapById(yap.id);
        if (freshYap) {
          // If it's a shout, fetch the original yap too to get fresh counts
          if (isShoutedYap(freshYap)) {
            const originalId = (freshYap as any).originalYapId || (freshYap as any).originalYap?.id;
            if (originalId) {
              const freshOriginal = await YapService.fetchYapById(originalId);
              if (freshOriginal) {
                freshYap.originalYap = freshOriginal;
              }
            }
          }
          // Preserve images from the initial yap prop if the fresh fetch has none.
          // Storage URLs don't change after posting; the feed already resolved them.
          if (!freshYap.images?.length && !freshYap.image) {
            if ((yap as any).images?.length) freshYap.images = (yap as any).images;
            else if ((yap as any).image) freshYap.image = (yap as any).image;
          }
          setDisplayYap(freshYap);
          
          // 🏠 RESIDENCY SYNC: Fetch the author's live profile to get current residency
          const authorId = freshYap.userId || freshYap.authorId || (freshYap as any).shoutedBy;
          if (authorId) {
            const { UserService } = await import('../services/api');
            const profile = await UserService.fetchUserById(String(authorId));
            if (profile) setAuthorProfile(profile);
          }

          // 🔒 SYNC: Clear optimistic deltas now that we have fresh server data
          window.dispatchEvent(new CustomEvent('refreshInteractions', { detail: { force: true } }));
          setIsLoadingYap(false);
        } else {
          setDisplayYap(null);
          setIsLoadingYap(false);
        }
      } catch (error) {
        console.error('[YapDetailScreen] Error refreshing yap data:', error);
        setIsLoadingYap(false);
      }
    };

    refreshYapData();
  }, [yap?.id]);



  // Handle null/undefined yap or missing properties.
  //
  // 🚫 A blocked author's yap is treated exactly like a missing one. Replies were
  // already filtered below, but the yap itself was not — so a deep link, a
  // notification, a share link or a stale feed entry rendered it in full.
  // Derived above the early returns below, because the effect that follows needs
  // them and a hook must not sit behind a conditional return. displayYap is null
  // until the fetch lands when the screen is opened from a shared link or a push
  // tap, so both guard against it.
  const isShout = displayYap ? isShoutedYap(displayYap) : false;
  const interactionId = displayYap ? getYapInteractionId(displayYap) : null;

  // Does anyone's shout of this yap carry a caption? Skipped entirely on a
  // shout's own detail page, where the button never renders anyway. Re-runs on
  // `yapUpdated` — the app's existing shout-changed signal, which UserDataContext
  // already uses to refetch shouts — so captioning a shout reveals the entry
  // point without needing a reload.
  //
  // This used to sit below the early returns. Opening a yap by id alone rendered
  // the loading branch first, skipping this hook, and the render that followed
  // ran one hook more than the last: "Rendered more hooks than during the
  // previous render", which took the whole app down to the error boundary. It
  // only ever fired on the id-only path, which is how shared links and push taps
  // arrive.
  useEffect(() => {
    if (isShout || !interactionId) { setHasCaptionedShouts(false); return; }
    let cancelled = false;
    const check = async () => {
      const has = await YapService.hasCaptionedShouts(String(interactionId));
      if (!cancelled) setHasCaptionedShouts(has);
    };
    void check();
    const onShoutsChanged = () => { void check(); };
    window.addEventListener('yapUpdated', onShoutsChanged);
    return () => { cancelled = true; window.removeEventListener('yapUpdated', onShoutsChanged); };
  }, [interactionId, isShout]);

  // The copy is deliberately identical to the not-found case: telling the viewer
  // "you blocked this user" would confirm the block to anyone who gets hold of
  // the account.
  if (!displayYap || isHiddenContent(displayYap)) {
    return (
      <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex items-center justify-center relative">
        <ParallaxBackground speed={0.12} />
        <div className="text-center relative z-10">
          <p className="text-[#373737] dark:text-[#E5E5E5] mb-4">Yap not found or deleted.</p>
          <Button
            onClick={onBack}
            className="bg-brand-gradient text-white shadow-lg px-6 py-2 rounded-xl font-bold transition-all active:scale-95"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // The transition overlay covers this; painting a second spinner underneath it
  // only shows up as a flash when the overlay lifts.
  if (isLoadingYap) return null;

  // Ensure default values for all required properties using unified utility
  const authorInfo = getYapAuthorInfo(displayYap, authorProfile || userProfile);
  const yapAuthor = authorInfo.name;
  const yapUsername = authorInfo.username;
  const yapAvatar = authorInfo.avatar;
  const yapRank = authorInfo.rank;
  
  // isShout and interactionId are derived above, alongside the effect that needs them.
  const originalYapId = isShout ? ((displayYap as any).originalYapId || (displayYap as any).originalYap?.id) : null;
  const isQuote = isQuoteShout(displayYap);

  // editedYapContent wins while it is set: the prop still carries the old text
  // until the feed refetches, and showing what you just typed is the point.
  const yapContent = (isShout && displayYap.shoutCaption)
    ? displayYap.shoutCaption
    : (editedYapContent ?? displayYap.content ?? '');
  
  // 🔒 FIX: Use formatFirestoreTime to gracefully handle both regular strings and standard Firestore Time objects
  const yapTime = formatFirestoreTime(displayYap.time || displayYap.timestamp);
  // Marked from the moment it is saved, not only after a refetch: editedYapContent
  // being set means this session did the editing.
  const yapWasEdited = !!(displayYap as any).edited || editedYapContent !== null;
  const yapLikes = (displayYap.likes || 0) as number;
  const yapComments = displayYap.comments || 0;
  
  // For shouts: primary image is shout images; for regular yaps, it's yap.image
  const yapImages: string[] = isShout
    ? (displayYap.shoutImages && displayYap.shoutImages.length > 0 ? displayYap.shoutImages : [])
    : (displayYap.images && displayYap.images.length > 0 ? displayYap.images : (displayYap.image ? [displayYap.image] : []));
  const yapImage = yapImages[0] || null;
  
  // For shouts: the shout count should come from the original yap, not the shout wrapper
  const yapShouts = isShout
    ? (displayYap.originalYap?.shouts || 0)
    : (displayYap.shouts || 0);

  const yapVisibility = displayYap.visibility || 'public';
  const yapCountry = authorInfo.country;
  const yapIsForumOnly = displayYap.isForumOnly || false;
  const yapIsAnonymous = displayYap.isAnonymous || false;
  const yapExpiresAt = displayYap.expiresAt;
  const yapAuthorId = isShout ? displayYap.shoutedBy : (displayYap.authorId || displayYap.userId);
  const isFriendAuthor = !yapIsAnonymous && !!yapAuthorId && String(yapAuthorId) !== String(userProfile?.id) && isFriend(String(yapAuthorId));

  // For shout detail: the original yap object
  const originalYap = isShout ? displayYap.originalYap : null;

  // Helper function to check if location should be shown for this yap
  const shouldShowLocation = () => {
    // 🔒 PATCH: Respect the shareLocation setting
    if (!shareLocation) return false;
    // Show location if yap has a country
    return !!yapCountry;
  };

  // Helper function to check if share/shout should be disabled for this yap
  const canShareOrShout = () => {
    // 🔒 SECURITY: Group yaps (Tribe/Forum) cannot be shouted or shared to World
    if (yap.tribeId || yap.forumId) {
      return false;
    }
    // Anonymous yaps cannot be shouted
    if (yapIsAnonymous) {
      return false;
    }
    // Check if the yap itself is friends-only
    if (yapVisibility === 'friends') {
      return false;
    }
    return true;
  };


  // Helper function to get time remaining for anonymous yap
  const getTimeRemaining = (expiresAt: string | Date) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  const handleToggleShout = () => {
    // For shouts, we need to check the original yap's ID
    const targetYapId = originalYapId || interactionId;
    
    // Check if yap is already shouted
    if (isYapShouted(targetYapId)) {
      // Open shout options modal to manage existing shout
      setSelectedYapToShout(isShout ? (yap as any).originalYap || yap : yap);
      setShowShoutOptionsModal(true);
    } else {
      // Open shout modal to create new shout
      setSelectedYapToShout(isShout ? (yap as any).originalYap || yap : yap);
      setShowShoutModal(true);
    }
  };

  const handleShoutWithCaption = async (caption: string, images: string[], friendsOnly: boolean, audioData?: { url: string, duration: number }) => {
    if (!yap) return;
    // friendsOnly must be forwarded — without it the shout was written as public
    // while the toast claimed "friends only".
    const ok = await toggleShoutGlobal(yap.id, caption, images, audioData, friendsOnly);
    // Announce it so the caption check above re-runs: this yap now has a
    // captioned shout, so the "View Shouts" entry point should appear.
    if (ok) window.dispatchEvent(new CustomEvent('yapUpdated'));
    setShowShoutModal(false);
    toast.success(friendsOnly ? 'Yap shouted to friends only!' : 'Yap shouted with your caption!', { duration: 1500 });
    setSelectedYapToShout(null);
  };

  const handleShoutWithoutCaption = () => {
    if (!yap) return;
    toggleShoutGlobal(yap.id);
    toast.success('Yap shouted to your profile!', { duration: 1500 });
    setShowShoutModal(false);
    setSelectedYapToShout(null);
  };

  const handleDeleteCaption = () => {
    if (selectedYapToShout) {
      updateShoutCaption(selectedYapToShout.id, undefined, undefined);
      toast.success('Caption deleted', { duration: 1500 });
    }
  };

  const handleDeleteShout = () => {
    if (selectedYapToShout) {
      // toggleShoutGlobal persists the unshout; removeShout would fire a second
      // concurrent unshoutYap for the same id. unshoutYap is now race-safe, but
      // there's no reason to pay for the duplicate round trip.
      toggleShoutGlobal(selectedYapToShout.id);
      removeShoutLocal(selectedYapToShout.id);
      toast.success('Shout removed', { duration: 1500 });
    }
  };

  const handleDeleteYapClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (reason: string) => {
    setIsDeleting(true);
    try {
      await YapService.deleteYap(yap.id);
      toast.success("Yap deleted successfully");
      setShowDeleteModal(false);
      onBack(); // Go back after deleting
      // Delay to allow Firestore batch update on shout docs to propagate
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('yapUpdated'));
      }, 800);
    } catch (error) {
      console.error("[YapDetailScreen] Failed to delete yap:", error);
      toast.error("Failed to delete yap");
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <div
      className="w-full h-screen relative bg-light-pattern dark:bg-[#0D0D0D] overflow-hidden"
      style={{ height: '100dvh' }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="pt-20 pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-[#F5F5DC]/80 dark:bg-[#121212] backdrop-blur-sm flex items-center justify-between"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 32px))' }}>
            <div className="flex items-center gap-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <h2 className="text-xl font-bold text-black dark:text-white truncate flex items-center gap-1">
              {isPinnedView && <Pin className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />}
              {isPinnedView ? 'Pinned Yap Details' : 'Yap Details'}
            </h2>
          </div>
          <div className="flex items-center gap-1">
          {String(yap.userId || yap.authorId) === String(userProfile.id) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLikers({ title: 'Liked your yap', load: () => YapService.getYapLikers(yap.id, (userProfile as any)?.ageGroup) })}
              className="h-8 px-2 text-xs font-medium text-black/60 dark:text-white/60 hover:text-[#E74C3C]"
            >
              See Likes
            </Button>
          )}
          {/* Shared menu. Every route into yap detail — feed, profile,
              notifications, search, shares, deep links — lands on this one
              component, so wiring PostMenu here covers all of them.
              Options come from postMenuOptions, not from this screen, which is
              why the hand-written list that used to live here (and that had
              drifted from the feed's) is gone. */}
          <PostMenu
            post={displayYap as any}
            surface="detail"
            collectionName={(displayYap as any)?.isShout ? 'shouts' : 'yaps'}
            onEditRequested={() => setEditingYapDetail(true)}
            onDeleteRequested={() => setShowDeleteModal(true)}
            onDeleted={() => onBack()}
            onSeeLikes={() => setLikers({
              title: 'Liked your yap',
              load: () => YapService.getYapLikers(yap.id, (userProfile as any)?.ageGroup),
            })}
            onLegionDelete={() => setLegionDeleteTarget({
              yapId: yap.id,
              authorId: String(yap.userId || yap.authorId),
              content: yapContent,
            })}
            onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
            className="h-8 w-8 p-0 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-black dark:text-white"
          />
          </div>
        </div>

        {/* minHeight: 0 inline, not the min-h-0 class, which is absent from the
            precompiled index.css. Without it flex-1 grows to the content height
            and nothing scrolls. See the matching note in ShoutDetailsScreen. */}
        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+120px)] overflow-x-hidden">
            {/* Original Yap */}
            <div className={`mb-6 p-4 rounded-2xl relative w-full overflow-hidden ${isPinnedView
              ? 'bg-linear-to-br from-[#F39C12]/10 dark:from-[#4478ea]/10 to-[#E74C3C]/10 dark:to-[#2d1ef1]/10 border-2 border-[#F39C12] dark:border-[#4478ea]'
              : 'bg-[#F5F5F5] dark:bg-white/5 border border-black/10 dark:border-white/10'
              }`}>
              {/* Pinned badge - only show when isPinnedView is true */}
              {isPinnedView && (
                <div className="absolute top-3 right-3">
                  <div className="bg-[#F39C12] dark:bg-[#4478ea] p-1.5 rounded-lg">
                    <Pin className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              {/* Author Info */}
              <div 
                className={`flex items-center gap-3 mb-4 ${!yapIsAnonymous ? 'cursor-pointer group' : ''}`}
                onClick={(e) => {
                  if (yapIsAnonymous) return;
                  e.stopPropagation();
                  // Navigate to user's profile
                  if (yapUsername === userProfile.username) {
                    window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'profile' }));
                  } else {
                    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { 
                      detail: {
                        id: yapAuthorId,
                        name: yapAuthor,
                        username: yapUsername,
                        image: yapAvatar
                      }
                    }));
                  }
                }}
              >
                {yapIsAnonymous ? (
                  <AnonymousAvatar className="w-12 h-12" />
                ) : (
                  <LegionAvatarWrapper rank={yapRank as UserRank} className="shadow-md">
                    <Avatar
                      className={`w-12 h-12 ${['legion'].includes(yapRank as string) ? 'border-none' : 'border-2 border-[#F39C12] dark:border-[#4478ea]'} ${getRankRingParams(yapRank as UserRank).ringClass}`}
                    >
                      <AvatarImage src={
                        yapUsername === userProfile.username
                          ? userProfile.portfolio[userProfile.profilePictureIndex || 0]
                          : yapAvatar
                      } />
                      <AvatarFallback>{yapAuthor[0]}</AvatarFallback>
                    </Avatar>
                  </LegionAvatarWrapper>
                )}
                <div className="flex-1 min-w-0 flex flex-col pt-0.5">
                  <div className="flex items-start justify-between min-h-[44px]">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      {/* Row 1: Name + Flag */}
                      <div className="flex items-center gap-1.5 min-h-[22px]">
                        <h4 className="flex items-center gap-1.5 font-bold text-[#1a1a1a] dark:text-white text-[17px] leading-tight truncate group-hover:underline">
                          <span>{yapIsAnonymous ? 'Anonymous' : yapAuthor}</span>
                          {!yapIsAnonymous && yapRank && (
                            <RankBadge rank={yapRank} size={20} />
                          )}
                        </h4>
                        {!yapIsAnonymous && (
                          <span className="flex items-center gap-1">
                            <span className="text-[11px] opacity-40 shrink-0">&bull;</span>
                            <FlagImage
                              countryName={yapCountry}
                              size="w80"
                              className="w-4 aspect-3/2 object-contain rounded shrink-0"
                            />
                          </span>
                        )}
                      </div>

                      {/* Row 2: @username • Time */}
                      <div className="flex items-center gap-1.5 text-xs text-[#8e8e8e] dark:text-[#a0a0a0] mt-0.5 min-w-0 flex-1">
                        <span className="font-normal truncate">
                          {yapIsAnonymous ? ANONYMOUS_USER.username : `@${yapUsername.replace(/^@+/, '')}`}
                        </span>
                        <span className="text-[8px] opacity-40 shrink-0">•</span>
                        <span className="whitespace-nowrap shrink-0">{yapTime}</span>
                        {yapWasEdited && (
                          <span className="whitespace-nowrap shrink-0 opacity-60">· edited</span>
                        )}
                        {yapIsAnonymous && yapExpiresAt && (
                          <span className="ml-1 text-[#F39C12] dark:text-[#4478ea] text-[10px] bg-[#F39C12]/10 dark:bg-[#4478ea]/10 px-1.5 rounded-full whitespace-nowrap">
                            Expires in {getTimeRemaining(yapExpiresAt)}
                          </span>
                        )}
                        {yapVisibility === 'friends' && (
                          <span className="flex items-center">
                            <span className="text-[8px] opacity-40 shrink-0 ml-1">•</span>
                            <div className="flex items-center gap-1 ml-1 text-[#E74C3C] dark:text-[#4478ea] shrink-0 drop-shadow-md">
                              <Lock className="w-3 h-3" />
                              <span className="text-xs uppercase font-bold">Friends Only</span>
                            </div>
                          </span>
                        )}
                      </div>

                      {/* Row 3: Relation badge (only when a relation exists) */}
                      {isFriendAuthor && (
                        <div className="mt-0.5">
                          <FriendBadge />
                        </div>
                      )}
                    </div>
                    {/* Right Side: Location Pin (Strict Residency Only) */}
                    {!yapIsAnonymous && authorInfo.residency && (
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        <MapPin className="w-3 h-3 text-[#4478ea] shrink-0" />
                        <span className="whitespace-nowrap font-medium text-xs text-[#666666] dark:text-[#a0a0a0]">
                          {authorInfo.residency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              {isShout && (
                <div className="flex items-center gap-1.5 mb-3 text-xs font-semibold text-[#4478ea] dark:text-[#4478ea]">
                  <Megaphone className="w-3.5 h-3.5 fill-[#4478ea] dark:fill-[#4478ea]" />
                  <span>Shouted</span>
                </div>
              )}

              <FormattedText 
                text={yapContent} 
                className="text-[#333333] dark:text-[#E5E5E5] mb-4 leading-relaxed whitespace-pre-wrap wrap-break-word block font-medium" 
              />
              
              {extractFirstUrl(yapContent) && (
                <div className="mb-4">
                  <LinkPreview url={extractFirstUrl(yapContent)!} />
                </div>
              )}

              {/* Poll. Yap rendering is duplicated per surface rather than shared,
                  so attaching a poll in the composer is not enough to make it
                  appear here — each surface has to render it. */}
              {(displayYap as any).poll?.options?.length > 0 && (
                <div className="mb-4">
                  <PollCard
                    yapId={String(displayYap.id)}
                    poll={(displayYap as any).poll}
                  />
                </div>
              )}

              {/* Deep links bypass the feed's filter, so the same bracket check
                  applies here. */}
              {(displayYap as any).roomShare?.roomId
                && sameBracket(userProfile, (displayYap as any).roomShare.ageGroup) && (
                <RoomShareCard
                  roomId={(displayYap as any).roomShare.roomId}
                  title={(displayYap as any).roomShare.title}
                />
              )}

              {/* Topic label (forum cross-post) — below the text, above media */}
              {displayYap.forumTopic?.forumId && (
                <div className="mb-4">
                  <YapTopicLabel
                    forumId={displayYap.forumTopic.forumId}
                    forumName={displayYap.forumTopic.forumName}
                  />
                </div>
              )}

              {yap.audioUrl && (
                <div className="mb-6">
                  <VoiceReplyBubble 
                    audioUrl={yap.audioUrl} 
                    duration={yap.duration}
                    className="w-full"
                  />
                </div>
              )}

              {/* Images — twitter-style tile grid */}
              {yapImages.filter(Boolean).length > 0 && (
                <div className="mb-6">
                  <YapImageGrid images={yapImages.filter(Boolean) as string[]} detail />
                </div>
              )}

              {/* Attached video (max 3 min, optional trim window). */}
              {(yap as any)?.videoUrl && (
                <div className="mb-6">
                  {/* Use the SAME sizing as the feed (aspect-fit, capped, left-
                      aligned) — not the full-width detail mode that letterboxed
                      vertical clips into a narrow strip with big black bars. */}
                  <YapVideoPlayer
                    videoUrl={(yap as any).videoUrl}
                    trimStart={(yap as any).videoTrimStart}
                    trimEnd={(yap as any).videoTrimEnd}
                    videoWidth={(yap as any).videoWidth}
                    videoHeight={(yap as any).videoHeight}
                    authorName={yapIsAnonymous ? 'Anonymous' : yapAuthor}
                  />
                </div>
              )}

              {/* Embedded Original Yap */}
              {isShout && originalYap && (
                <EmbeddedYap
                  yapId={originalYapId || ''}
                  username={originalYap.username || originalYap.userUsername || '@unknown'}
                  authorName={originalYap.author || originalYap.userName || 'Unknown'}
                  avatar={originalYap.avatar || originalYap.userAvatar || ''}
                  country={originalYap.countryFlag || originalYap.country}
                  timestamp={originalYap.timestamp || originalYap.time || ''}
                  content={originalYap.content || ''}
                  image={originalYap.image || (originalYap.images && originalYap.images[0])}
                  images={originalYap.images || (originalYap.image ? [originalYap.image] : [])}
                  likes={originalYap.likes || 0}
                  comments={originalYap.replies || originalYap.comments || 0}
                  shouts={originalYap.shouts || 0}
                  isLiked={isPostLiked(originalYap.id || '', 'yap')}
                  isShout={true}
                  isDeleted={originalYap.deleted}
                  showActions={false}
                  showLocation={originalYap.showLocation ?? true}
                  onYapClick={(e) => {
                    e?.stopPropagation();
                    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: originalYap }));
                  }}
                  className="mb-5"
                  minimal={true}
                />
              )}

              {/* View Shouts — sits below the media and above the engagement row.
                  Hidden on a shout's own detail page: `displayYap` is the shout
                  there, and a shout is not itself shoutable, so the list would
                  either be empty or (worse) show the original's shouts under a
                  heading that reads as though they were this shout's.

                  Gated on a shout that actually carries a caption, not on the
                  shout counter: the counter includes simple reposts, which have
                  nothing written to read, and it drifts besides. */}
              {!isShout && canShareOrShout() && hasCaptionedShouts && (
                <div className="mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.dispatchEvent(
                      new CustomEvent(EVENTS.NAVIGATE_TO_YAP_SHOUTS, { detail: String(interactionId) })
                    )}
                    className="flex items-center gap-2 h-auto py-1 font-semibold"
                    // Inline: `text-[#4478ea]` has no rule in the compiled
                    // stylesheet this app ships, so the blue has to be set here
                    // or the label renders in the default foreground colour.
                    style={{ color: '#4478ea', paddingLeft: 0, paddingRight: 0 }}
                  >
                    <Megaphone className="w-4 h-4" />
                    View Shouts
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-4">
                {!isTribePost && (
                <LikeButton
                  postId={interactionId}
                  postType={getYapPostType(displayYap)}
                  originalLikeCount={yapLikes || 0}
                  canInteract={() => isGroupInteractionAllowed}
                  onBlockedInteraction={() => {
                    toast.error("Members only", { 
                      description: `Join this ${displayYap.tribeId ? 'tribe' : 'forum'} to like yaps.` 
                    });
                  }}
                  className="px-3 py-1.5"
                  iconSize={20}
                />
                )}

                {canShareOrShout() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        if (!isGroupInteractionAllowed) {
                            toast.error("Members only", { description: "Join this group to shout." });
                            return;
                        }
                        handleToggleShout();
                    }}
                    className={`flex items-center gap-2 h-auto px-3 py-1.5 ${!isGroupInteractionAllowed ? 'opacity-50 text-[#373737] dark:text-[#E5E5E5]' : isYapShouted(interactionId) ? 'text-[#F39C12] dark:text-[#4478ea] hover:text-[#F39C12] dark:hover:text-[#4478ea]' : 'text-[#373737] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'}`}
                  >
                    <Megaphone className={`w-5 h-5 ${isYapShouted(interactionId) ? 'fill-[#F39C12] dark:fill-[#4478ea] text-[#F39C12] dark:text-[#4478ea]' : ''}`} />
                    <span>{getShoutCount(yapShouts, interactionId)}</span>
                  </Button>
                )}

                {!isTribePost && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!isGroupInteractionAllowed) {
                      toast.error("Members only");
                    } else {
                      setShowReplyBox(!showReplyBox);
                    }
                  }}
                  className={`flex items-center gap-2 h-auto px-3 py-1.5 ${!isGroupInteractionAllowed ? 'text-[#999999] opacity-50 cursor-not-allowed' : 'text-[#373737] dark:text-[#E5E5E5] hover:text-[#F39C12]'}`}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>{getPostReplyCount(yapComments, interactionId, getYapPostType(displayYap))}</span>
                </Button>
                )}

                {canShareOrShout() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!isGroupInteractionAllowed) {
                        toast.error("Members only");
                        return;
                      }
                      setShowShareModal(true);
                    }}
                    className={`flex items-center gap-2 h-auto px-3 py-1.5 text-[#373737] dark:text-[#E5E5E5] hover:text-[#F39C12] ${!isGroupInteractionAllowed ? 'opacity-50' : ''}`}
                  >
                    <Share2 className="w-5 h-5" />
                  </Button>
                )}

              </div>
            </div>

            <PostReplies
              postId={String(yap.id)}
              countId={String(interactionId)}
              countType={getYapPostType(displayYap)}
              opAuthorId={yapAuthorId}
              postOwnerId={yap.userId || yap.authorId}
              isAnonymousPost={yapIsAnonymous}
              canInteract={isGroupInteractionAllowed}
              repliesDisabled={isTribePost}
              showReplyBox={showReplyBox}
              onShowReplyBoxChange={setShowReplyBox}
              onRepliesChanged={onYapUpdate}
            />
          </div>
        </ScrollArea>
      </div>

      {/* Modals */}
      {selectedYapToShout && (
        <ShoutYapModal
          isOpen={showShoutModal}
          onClose={() => {
            setShowShoutModal(false);
            setSelectedYapToShout(null);
          }}
          yap={selectedYapToShout}
          onShoutWithCaption={handleShoutWithCaption}
          onShoutWithoutCaption={handleShoutWithoutCaption}
        />
      )}
      {selectedYapToShout && (
        <ShoutedYapOptionsModal
          isOpen={showShoutOptionsModal}
          onClose={() => {
            setShowShoutOptionsModal(false);
            setSelectedYapToShout(null);
          }}
          yap={selectedYapToShout}
          onDeleteCaption={handleDeleteCaption}
          onDeleteShout={handleDeleteShout}
        />
      )}
      <ShareYapModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        yap={yap}
      />
      <DeleteYapModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        isSelfDelete={!!yap && String(yap.userId || yap.authorId) === String(userProfile.id)}
      />
      <LegionDeleteModal
        isOpen={!!legionDeleteTarget}
        onClose={() => setLegionDeleteTarget(null)}
        yapId={legionDeleteTarget?.yapId || ''}
        yapContent={legionDeleteTarget?.content || ''}
        authorId={legionDeleteTarget?.authorId || ''}
        moderatorId={String(userProfile.id)}
        onDeleted={() => {
          setLegionDeleteTarget(null);
          onBack();
          // Delay to allow feed to refresh
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('yapDeleted', { detail: { yapId: yap.id } }));
          }, 500);
        }}
      />
      <LikersModal
        open={!!likers}
        onClose={() => setLikers(null)}
        userIds={likers?.userIds}
        loadUserIds={likers?.load}
        title={likers?.title}
      />
      {/* One yap, one reply — the same modal, differing only by which id it is given. */}
      <EditPostModal
        open={editingYapDetail}
        onClose={() => setEditingYapDetail(false)}
        postId={String((yap as any)?.id || '')}
        initialContent={String(editedYapContent ?? (yap as any)?.content ?? '')}
        timestamp={(yap as any)?.timestamp}
        onSaved={(next) => setEditedYapContent(next)}
      />

    </div>
  );
}
